import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

const SpectrumNoise = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volumes, setVolumes] = useState({ white: 0, pink: 0, brown: 0 });
  const audioContextRef = useRef(null);
  const nodesRef = useRef({});

  // Cleanup function to stop all audio
  const stopAllAudio = useCallback(() => {
    // Stop all gain nodes
    Object.values(nodesRef.current).forEach(({ gainNode }) => {
      if (gainNode) {
        gainNode.gain.value = 0;
      }
    });
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.log('Audio context already closed');
      }
      audioContextRef.current = null;
    }
    
    nodesRef.current = {};
  }, []);

  // Cleanup on component unmount OR when navigating away
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, [stopAllAudio]);

  const initAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      const createNoise = (type) => {
        const bufferSize = 4096;
        const node = audioContextRef.current.createScriptProcessor(bufferSize, 1, 1);
        const gainNode = audioContextRef.current.createGain();
        gainNode.gain.value = 0;
        
        let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
        let lastOut = 0;
        
        node.onaudioprocess = (e) => {
          const output = e.outputBuffer.getChannelData(0);
          
          if (type === 'white') {
            for (let i = 0; i < bufferSize; i++) {
              output[i] = Math.random() * 2 - 1;
            }
          } else if (type === 'pink') {
            for (let i = 0; i < bufferSize; i++) {
              const white = Math.random() * 2 - 1;
              b0 = 0.99886 * b0 + white * 0.0555179;
              b1 = 0.99332 * b1 + white * 0.0750759;
              b2 = 0.96900 * b2 + white * 0.1538520;
              b3 = 0.86650 * b3 + white * 0.3104856;
              b4 = 0.55000 * b4 + white * 0.5329522;
              b5 = -0.7616 * b5 - white * 0.0168980;
              output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
              output[i] *= 0.11;
              b6 = white * 0.115926;
            }
          } else if (type === 'brown') {
            for (let i = 0; i < bufferSize; i++) {
              const white = Math.random() * 2 - 1;
              output[i] = (lastOut + (0.02 * white)) / 1.02;
              lastOut = output[i];
              output[i] *= 3.5;
            }
          }
        };
        
        node.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        return { node, gainNode };
      };

      nodesRef.current = {
        white: createNoise('white'),
        pink: createNoise('pink'),
        brown: createNoise('brown')
      };
    }
  };

  const togglePlay = () => {
    if (!isPlaying) {
      initAudio();
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      setIsPlaying(true);
    } else {
      // Stop playing
      setIsPlaying(false);
      setVolumes({ white: 0, pink: 0, brown: 0 });
      stopAllAudio();
    }
  };

  // Custom back handler that ensures audio stops
  const handleBack = () => {
    stopAllAudio();
    setIsPlaying(false);
    navigate('/dashboard');
  };

  const handleVolumeChange = (type, value) => {
    const vol = value[0] / 100;
    setVolumes(prev => ({ ...prev, [type]: value[0] }));
    if (nodesRef.current[type]) {
      const { gainNode } = nodesRef.current[type];
      const ctx = audioContextRef.current;
      if (ctx) {
        // Ramp instead of stepping instantly to avoid audible clicking/zipper noise
        gainNode.gain.cancelScheduledValues(ctx.currentTime);
        gainNode.gain.setTargetAtTime(vol, ctx.currentTime, 0.015);
      } else {
        gainNode.gain.value = vol;
      }
    }
  };

  const noiseTypes = [
    { id: 'white', name: 'White Noise', color: '#F8F9FA', textColor: '#333', description: 'All frequencies equally - like TV static' },
    { id: 'pink', name: 'Pink Noise', color: '#FFC8DD', textColor: '#333', description: 'Balanced, natural sound - like rainfall' },
    { id: 'brown', name: 'Brown Noise', color: '#D7B299', textColor: '#333', description: 'Deep, rumbling - like thunder or waterfall' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] to-[#E0F7FA] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-purple-300 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-pink-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={handleBack}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Spectrum Noise</h1>
          
          <button
            data-testid="toggle-play"
            onClick={togglePlay}
            className={`px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-lg flex items-center gap-2 ${
              isPlaying ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#81B29A] hover:bg-[#6A9B84] text-white'
            }`}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Stop' : 'Play'}
          </button>
        </header>

        {isPlaying && (
          <div className="mb-6 bg-green-100 border border-green-300 rounded-xl p-4 text-center">
            <p className="text-green-800 font-medium">
              🔊 Audio is playing - Use the Stop button or Back button to stop the sound
            </p>
          </div>
        )}

        <div className="space-y-8">
          {noiseTypes.map(noise => (
            <div
              key={noise.id}
              className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100"
              style={{ borderLeftWidth: '8px', borderLeftColor: noise.color }}
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-[#2D3748] mb-1">{noise.name}</h3>
                  <p className="text-[#4A5568]">{noise.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  {volumes[noise.id] > 0 && (
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                  )}
                  <div className="text-3xl font-bold text-[#81B29A]">{volumes[noise.id]}%</div>
                </div>
              </div>
              
              <Slider
                data-testid={`${noise.id}-slider`}
                value={[volumes[noise.id]]}
                onValueChange={(value) => handleVolumeChange(noise.id, value)}
                max={100}
                step={1}
                disabled={!isPlaying}
                className={`w-full ${!isPlaying ? 'opacity-50' : ''}`}
              />
            </div>
          ))}
        </div>

        <div className="mt-12 bg-[#E0F7FA] rounded-2xl p-6 text-center">
          <p className="text-[#4A5568] text-lg">
            {isPlaying 
              ? "Adjust the sliders to mix your perfect sound. The Stop button will silence all sounds."
              : "Press Play to start, then mix different noise types to find your perfect focus or sleep sound."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpectrumNoise;
